// Operator Group Service - API interactions for operator group management

import axios, { AxiosInstance } from 'axios';
import {
  OperatorGroup,
  OperatorGroupDTO,
  Worker,
  Division,
  CreateOperatorGroupCommand,
  UpdateOperatorGroupCommand,
  DeleteOperatorGroupCommand,
  BatchUpdateOperatorGroupStatusCommand,
  PageOperatorGroupRequest,
  PageWorkerRequest,
  PageDivisionRequest,
} from '../types/operatorGroup';
import { ApiResponse, PaginatedResponse } from '../types/index';
import { createSuccessResponse, createErrorResponse } from '../utils/responseTransformer';
import { API_ENDPOINTS } from '../utils/apiEndpoints';
import httpClient from './httpClient';
import { WHOLE_USE_MOCK } from './api';


/**
 * Service class for Operator Group operations
 * Supports both Mock data and real API calls
 */
export class OperatorGroupService {
  private static instance: OperatorGroupService;
  
  // Static configuration to control whether to use mock data
  private static useMock: boolean = true;

  /**
   * Get singleton instance
   */
  public static getInstance(): OperatorGroupService {
    if (!OperatorGroupService.instance) {
      OperatorGroupService.instance = new OperatorGroupService();
    }
    return OperatorGroupService.instance;
  }

  /**
   * Configure whether to use mock data
   * @param useMock Whether to use mock data
   */
  public static setUseMock(useMock: boolean): void {
    OperatorGroupService.useMock = useMock;
  }

  /**
   * Get current mock usage setting
   */
  public static getUseMock(): boolean {
    return OperatorGroupService.useMock;
  }

  /**
   * Simulate API delay for development
   * @param ms Milliseconds to delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * 分页查询操作组
   */
  async pageQueryOperatorGroups(query: PageOperatorGroupRequest): Promise<ApiResponse<PaginatedResponse<OperatorGroupDTO>>> {
    try {
      if (OperatorGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(800);
        
        // Import mock data dynamically
        const { getMockOperatorGroupListResponse } = await import('../mock/operatorGroupData');
        return getMockOperatorGroupListResponse(query);
      } else {
        // Call real API
        const response = await httpClient.post(API_ENDPOINTS.OPERATOR_GROUP.PAGE, query);
        return response.data;
      }
    } catch (error) {
      console.error('分页查询操作组失败:', error);
      return createErrorResponse('SERVER_ERROR', 'QUERY_FAILED');
    }
  }

  /**
   * 查询操作组
   */
  async listOperatorGroups(query: any): Promise<ApiResponse<OperatorGroupDTO[]>> {
    try {
      if (OperatorGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(800);
        
        // Import mock data dynamically
        const { getMockOperatorGroupListOnlyResponse } = await import('../mock/operatorGroupData');
        return getMockOperatorGroupListOnlyResponse(query);
      } else {
        // Call real API
        const response = await httpClient.post(API_ENDPOINTS.OPERATOR_GROUP.PAGE, query);
        return response.data;
      }
    } catch (error) {
      console.error('分页查询操作组失败:', error);
      return createErrorResponse('SERVER_ERROR', 'QUERY_FAILED');
    }
  }

  /**
   * 根据编码查询操作组
   */
  async getOperatorGroupByCode(code: string): Promise<ApiResponse<OperatorGroup>> {
    try {
      if (OperatorGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(600);
        
        // Import mock data dynamically
        const { getMockOperatorGroupDetailResponse } = await import('../mock/operatorGroupData');
        return getMockOperatorGroupDetailResponse(code);
      } else {
        // Call real API
        const response = await httpClient.get(API_ENDPOINTS.OPERATOR_GROUP.QUERY_BY_CODE + '?code=' + code);
        return response.data;
      }
    } catch (error) {
      console.error('根据编码查询操作组失败:', error);
      return createErrorResponse('SERVER_ERROR', 'QUERY_FAILED');
    }
  }

  /**
   * 创建操作组
   */
  async createOperatorGroup(command: CreateOperatorGroupCommand): Promise<ApiResponse<void>> {
    try {
      if (OperatorGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(1000);
        
        // Import mock data dynamically
        const { getMockOperatorGroupCreateResponse } = await import('../mock/operatorGroupData');
        return getMockOperatorGroupCreateResponse(command);
      } else {
        // Call real API
        const response = await httpClient.post(API_ENDPOINTS.OPERATOR_GROUP.CREATE, command);
        return response.data;
      }
    } catch (error) {
      console.error('创建操作组失败:', error);
      return createErrorResponse('SERVER_ERROR', 'CREATE_FAILED');
    }
  }

  /**
   * 更新操作组
   */
  async updateOperatorGroup(command: UpdateOperatorGroupCommand): Promise<ApiResponse<void>> {
    try {
      if (OperatorGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(1000);
        
        // Import mock data dynamically
        const { getMockOperatorGroupUpdateResponse } = await import('../mock/operatorGroupData');
        return getMockOperatorGroupUpdateResponse(command);
      } else {
        // Call real API
        const response = await httpClient.post(API_ENDPOINTS.OPERATOR_GROUP.UPDATE, command);
        return response.data;
      }
    } catch (error) {
      console.error('更新操作组失败:', error);
      return createErrorResponse('SERVER_ERROR', 'UPDATE_FAILED');
    }
  }

  /**
   * 删除操作组
   */
  async deleteOperatorGroup(command: DeleteOperatorGroupCommand): Promise<ApiResponse<boolean>> {
    try {
      if (OperatorGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(1000);
        
        // Import mock data dynamically
        const { getMockOperatorGroupDeleteResponse } = await import('../mock/operatorGroupData');
        return getMockOperatorGroupDeleteResponse(command);
      } else {
        // Call real API
        const response = await httpClient.post(API_ENDPOINTS.OPERATOR_GROUP.DELETE, command);
        return response.data;
      }
    } catch (error) {
      console.error('删除操作组失败:', error);
      return createErrorResponse('SERVER_ERROR', 'DELETE_FAILED');
    }
  }

  /**
   * 批量更新操作组状态
   */
  async batchUpdateOperatorGroupStatus(command: BatchUpdateOperatorGroupStatusCommand): Promise<ApiResponse<{ successCount: number; failureCount: number }>> {
    try {
      if (OperatorGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(1500);
        
        // Import mock data dynamically
        const { getMockBatchUpdateOperatorGroupStatusResponse } = await import('../mock/operatorGroupData');
        return getMockBatchUpdateOperatorGroupStatusResponse(command);
      } else {
        // Call real API
        let req = {ids: command.codes}
        if ("ENABLE" === command.status) {
          const response = await httpClient.post(API_ENDPOINTS.OPERATOR_GROUP.BATCH_ENABLE, req);
          return response.data;
        } else {
          const response = await httpClient.post(API_ENDPOINTS.OPERATOR_GROUP.BATCH_DISENABLE, req);
          return response.data;
        }
      }
    } catch (error) {
      console.error('批量更新操作组状态失败:', error);
      return createErrorResponse('SERVER_ERROR', 'BATCH_UPDATE_FAILED');
    }
  }


  /**
   * 检查操作组编码唯一性
   */
  async checkCodeUniqueness(code: string): Promise<ApiResponse<boolean>> {
    try {
      if (OperatorGroupService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(300);
        
        // Import mock data dynamically
        const { mockOperatorGroups } = await import('../mock/operatorGroupData');
        const exists = mockOperatorGroups.some(group => group.code === code && group.deleted === 0);
        return createSuccessResponse(!exists);
      } else {
        // Call real API
        const response = await httpClient.get(
          API_ENDPOINTS.OPERATOR_GROUP.QUERY_BY_CODE + '?code=' + code
        );
        // 如果找到记录，说明编码已存在
        return createSuccessResponse(!response.data.data);
      }
    } catch (error) {
      console.error('检查操作组编码唯一性失败:', error);
      return createErrorResponse('SERVER_ERROR', 'VALIDATION_FAILED');
    }
  }
}

// Export singleton instance
export default OperatorGroupService.getInstance();